import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ShoppingCart, Plus, Minus, Trash2, Share2, Link2, RefreshCw, Copy } from 'lucide-react';
import { useCart } from '@/hooks/useCart';
import { toast } from 'sonner';

export const CartDrawer: React.FC = () => {
  const { items, total, itemCount, updateQuantity, removeItem, clearCart, getShareLink, isLoading } = useCart();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleShare = async () => {
    const link = await getShareLink();
    if (link) {
      setShareUrl(link);
      await navigator.clipboard.writeText(link);
      toast.success('Cart link copied to clipboard!');
    } else {
      toast.error('Please log in to share your cart');
    }
  };

  const subscriptionItems = items.filter(i => i.is_subscription);
  const oneTimeItems = items.filter(i => !i.is_subscription);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary text-primary-foreground">
              {itemCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Shopping Cart ({itemCount})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Your cart is empty</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* One-time purchases */}
            {oneTimeItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">One-Time Purchase</h3>
                {oneTimeItems.map(item => (
                  <CartItemRow key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} />
                ))}
              </div>
            )}

            {/* Subscriptions */}
            {subscriptionItems.length > 0 && (
              <div>
                <Separator className="my-4" />
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Subscriptions
                </h3>
                {subscriptionItems.map(item => (
                  <CartItemRow key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} />
                ))}
              </div>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1 gap-2" size="lg">
                <ShoppingCart className="h-4 w-4" />
                Checkout
              </Button>
              <Button variant="outline" size="lg" onClick={handleShare} title="Share cart">
                <Share2 className="h-4 w-4" />
              </Button>
            </div>

            {shareUrl && (
              <div className="flex items-center gap-2 bg-muted rounded-lg p-2 text-xs">
                <Link2 className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                <span className="truncate flex-1">{shareUrl}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Copied!'); }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}

            <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => clearCart()}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Clear Cart
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const CartItemRow: React.FC<{
  item: any;
  onUpdateQuantity: (params: { itemId: string; quantity: number }) => void;
  onRemove: (id: string) => void;
}> = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="flex gap-3 py-3 border-b border-border last:border-0">
    {item.product_image && (
      <img src={item.product_image} alt={item.product_name || ''} className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">{item.product_name}</p>
      {item.is_subscription && item.subscription_period && (
        <Badge variant="secondary" className="text-xs mt-0.5">
          <RefreshCw className="h-2.5 w-2.5 mr-1" />
          Every {item.subscription_interval || 1} {item.subscription_period}
        </Badge>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => onUpdateQuantity({ itemId: item.id, quantity: item.quantity - 1 })}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="text-sm w-6 text-center">{item.quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6"
            onClick={() => onUpdateQuantity({ itemId: item.id, quantity: item.quantity + 1 })}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">${((item.product_price || 0) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onRemove(item.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);
